import React, { useState } from 'react';

interface BuilderElement {
  component?: { name?: string; options?: { text?: string } };
  children?: BuilderElement[];
}

interface AccordionItem {
  title?: BuilderElement[];
  detail?: BuilderElement[];
}

interface BuilderAccordionProps {
  items?: AccordionItem[];
}

/** Recursively extract visible text from Builder SDK element trees. */
function extractHtml(elements: BuilderElement[] = []): string {
  const parts: string[] = [];
  function walk(blocks: BuilderElement[]) {
    for (const b of blocks) {
      const text = b.component?.options?.text;
      // Skip the chevron-only Text nodes (just '⌃')
      if (text && text.replace(/<[^>]+>/g, '').trim() !== '⌃') {
        parts.push(text);
      }
      if (b.children?.length) walk(b.children);
    }
  }
  walk(elements);
  return parts.join('');
}

/**
 * Native React accordion registered as "Builder:Accordion".
 * Builder stores each item as `{ title: BuilderElement[], detail: BuilderElement[] }`.
 * We extract the HTML from those elements and render a clean expand/collapse UI.
 */
const BuilderAccordion: React.FC<BuilderAccordionProps> = ({ items = [] }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="w-full divide-y divide-gray-700">
      {items.map((item, i) => {
        const titleHtml = extractHtml(item.title);
        const detailHtml = extractHtml(item.detail);
        const isOpen = openIndex === i;

        return (
          <div key={i}>
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              className="w-full flex items-center justify-between py-4 px-2 text-left text-white hover:text-emerald-400 transition-colors"
            >
              <span
                className="text-base font-medium pr-4"
                dangerouslySetInnerHTML={{ __html: titleHtml }}
              />
              <svg
                className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isOpen && (
              <div
                className="px-2 pb-5 text-gray-300 text-sm leading-relaxed prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: detailHtml }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BuilderAccordion;
